import React, { useState } from 'react';
import { 
  Link, 
  Clock, 
  Info, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Lock, 
  Unlock, 
  FileText, 
  RefreshCw,
  Zap
} from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения результатов проверки URL
 */
const ResultsViewer = ({ results }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedHop, setSelectedHop] = useState(0);

  if (!results || !results.chain || results.chain.length === 0) {
    return null;
  }

  const { chain, summary } = results;

  // Определение статуса результата для цветовой индикации
  const getStatusInfo = (status) => {
    if (status === 0) {
      return {
        color: 'red',
        icon: <XCircle className="w-5 h-5 text-red-500" />,
        text: 'Ошибка соединения'
      };
    } else if (status === 200) {
      return {
        color: 'green',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        text: 'OK'
      };
    } else if (status >= 300 && status < 400) {
      return {
        color: 'blue',
        icon: <RefreshCw className="w-5 h-5 text-blue-500" />,
        text: 'Редирект'
      };
    } else if (status >= 400 && status < 500) {
      return {
        color: 'orange',
        icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
        text: 'Клиентская ошибка'
      };
    } else if (status >= 500) {
      return {
        color: 'red',
        icon: <XCircle className="w-5 h-5 text-red-500" />,
        text: 'Серверная ошибка'
      };
    } else {
      return {
        color: 'gray',
        icon: <Info className="w-5 h-5 text-gray-500" />,
        text: 'Неизвестный статус'
      };
    }
  };

  // Форматирование URL
  const formatUrl = (url) => {
    try {
      if (!url) return '';
      const urlObj = new URL(url);
      
      // Сокращаем очень длинные URL
      let formatted = url;
      if (url.length > 50) {
        formatted = `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname.slice(0, 15)}...`;
        if (urlObj.search) formatted += '?...';
      }
      
      return formatted;
    } catch (e) {
      return url;
    }
  };

  // Форматирование времени
  const formatTime = (ms) => {
    if (ms < 0) return 'н/д';
    if (ms < 1000) return `${ms} мс`;
    return `${(ms / 1000).toFixed(2)} с`;
  };

  // Рендер метки индикатора SEO-влияния
  const renderSeoRating = (rating) => {
    const colors = {
      excellent: 'bg-green-500',
      good: 'bg-green-400',
      fair: 'bg-yellow-400',
      poor: 'bg-orange-500',
      bad: 'bg-red-500'
    };

    return (
      <div className="flex items-center mt-2">
        <div className={`w-3 h-3 rounded-full ${colors[rating.score]} mr-2`}></div>
        <span className="text-sm font-medium">{rating.label}</span>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Zap className="w-5 h-5 mr-2 text-indigo-500" />
        Результаты проверки
      </h3>

      {/* Табы для результатов */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('summary')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'summary'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Сводка
        </button>
        <button
          onClick={() => setActiveTab('headers')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'headers'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Заголовки
        </button>
        <button
          onClick={() => setActiveTab('timing')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'timing'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Скорость загрузки
        </button>
        {chain[chain.length - 1].meta && (
          <button
            onClick={() => setActiveTab('meta')}
            className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
              activeTab === 'meta'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Meta-теги
          </button>
        )}
      </div>

      {/* Список перенаправлений */}
      <div className="mb-5 border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-700/30">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Цепочка перенаправлений ({chain.length} {chain.length === 1 ? 'запрос' : 
            chain.length < 5 ? 'запроса' : 'запросов'})
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar pr-1">
          {chain.map((hop, index) => {
            const statusInfo = getStatusInfo(hop.status);
            
            return (
              <button
                key={index}
                onClick={() => setSelectedHop(index)}
                className={`w-full text-left p-2 rounded-md flex items-start transition-colors ${
                  selectedHop === index 
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700' 
                    : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex-shrink-0 mr-3 mt-1">
                  {statusInfo.icon}
                </div>
                <div className="flex-grow">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {hop.status} {hop.statusText}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(hop.timing?.total || 0)}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                    {formatUrl(hop.url)}
                  </div>
                </div>
                <div className="ml-2 flex-shrink-0 text-xs font-medium rounded px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  {index + 1}
                </div>
                </button>
            );
          })}
        </div>
      </div>

      {/* Сводка результатов */}
      {activeTab === 'summary' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Исходный URL</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white break-all">{summary.initialUrl}</div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Итоговый URL</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white break-all">{summary.finalUrl}</div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Количество редиректов</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{summary.redirectCount}</div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Общее время загрузки</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{formatTime(summary.totalTime)}</div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Статус</div>
                <div className="text-sm font-medium flex items-center">
                  {getStatusInfo(chain[chain.length - 1].status).icon}
                  <span className="ml-2">{chain[chain.length - 1].status} {chain[chain.length - 1].statusText}</span>
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Тип соединения</div>
                <div className="text-sm font-medium flex items-center">
                  {summary.isSecureConnection ? 
                    <><Lock className="w-4 h-4 text-green-500 mr-2" /> HTTPS (защищенное)</> : 
                    <><Unlock className="w-4 h-4 text-orange-500 mr-2" /> HTTP (не защищенное)</>}
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Смена домена</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {summary.isSameDomain ? "Нет (редирект в пределах одного домена)" : "Да (редирект на другой домен)"}
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Влияние на SEO</div>
                {renderSeoRating(summary.seoImpact)}
                <ul className="mt-2 text-xs text-gray-600 dark:text-gray-400 pl-4 list-disc">
                  {summary.seoImpact.reasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Заголовки */}
      {activeTab === 'headers' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          {chain[selectedHop] && (
            <>
              <h4 className="font-medium text-sm mb-3 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-indigo-500" />
                Заголовки для запроса {selectedHop + 1}: {chain[selectedHop].url.split('/')[2]}
              </h4>
              
              {Object.keys(chain[selectedHop].headers || {}).length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Нет доступных заголовков
                </div>
              ) : (
                <div className="overflow-y-auto max-h-80 custom-scrollbar border border-gray-200 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-700/20">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                      <tr>
                        <th className="text-left py-2 pl-2">Заголовок</th>
                        <th className="text-left py-2 pl-2">Значение</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(chain[selectedHop].headers || {}).map(([key, value], index) => (
                        <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-700/30">
                          <td className="py-2 pl-2 pr-4 align-top font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{key}</td>
                          <td className="py-2 pl-2 text-gray-600 dark:text-gray-400 break-all">
                            {Array.isArray(value) ? value.map((v, i) => <div key={i}>{v}</div>) : value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Скорость загрузки */}
      {activeTab === 'timing' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          {chain[selectedHop] && chain[selectedHop].timing && (
            <>
              <h4 className="font-medium text-sm mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-indigo-500" />
                Время загрузки для запроса {selectedHop + 1}: {chain[selectedHop].url.split('/')[2]}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="mb-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Общее время</div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white">{formatTime(chain[selectedHop].timing.total)}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">DNS-запрос</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{formatTime(chain[selectedHop].timing.dns)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Установка соединения</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{formatTime(chain[selectedHop].timing.connection)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">TLS/SSL</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{formatTime(chain[selectedHop].timing.tls)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Время до первого байта (TTFB)</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{formatTime(chain[selectedHop].timing.ttfb)}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Визуализация временных затрат</div>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>DNS</span>
                        <span>{formatTime(chain[selectedHop].timing.dns)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (chain[selectedHop].timing.dns / chain[selectedHop].timing.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>Соединение</span>
                        <span>{formatTime(chain[selectedHop].timing.connection)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className="bg-green-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (chain[selectedHop].timing.connection / chain[selectedHop].timing.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>TLS/SSL</span>
                        <span>{formatTime(chain[selectedHop].timing.tls)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className="bg-purple-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (chain[selectedHop].timing.tls / chain[selectedHop].timing.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>TTFB</span>
                        <span>{formatTime(chain[selectedHop].timing.ttfb)}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div 
                          className="bg-orange-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (chain[selectedHop].timing.ttfb / chain[selectedHop].timing.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">IP-адрес</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{chain[selectedHop].ip || 'Н/Д'}</div>
                  </div>
                  
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Геолокация</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{chain[selectedHop].location || 'Н/Д'}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Мета-теги */}
      {activeTab === 'meta' && chain[chain.length - 1].meta && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="font-medium text-sm mb-3 flex items-center">
            <Info className="w-4 h-4 mr-2 text-indigo-500" />
            Meta-теги итоговой страницы
          </h4>
          
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Title</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {chain[chain.length - 1].meta.title || 'Отсутствует'}
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Description</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {chain[chain.length - 1].meta.description || 'Отсутствует'}
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Canonical URL</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white break-all">
                {chain[chain.length - 1].meta.canonical || 'Отсутствует'}
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Robots</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {chain[chain.length - 1].meta.robotsDirectives || 'Отсутствует'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Кнопки действий */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          variant="outline"
          icon={<Link className="w-4 h-4" />}
          onClick={() => {
            if (chain && chain.length > 0) {
              const finalUrl = chain[chain.length - 1].url;
              window.open(finalUrl, '_blank');
            }
          }}
        >
          Открыть итоговый URL
        </Button>
        
        <Button
          variant="outline"
          icon={<Clock className="w-4 h-4" />}
          onClick={() => setActiveTab('timing')}
        >
          Анализ скорости
        </Button>
      </div>
    </div>
  );
};

export default ResultsViewer;